import { m } from "@estate-platform/i18n/messages";
import { createContext, Suspense, use, useCallback, useEffect, useMemo, useState } from "react";

import { useDataLayer } from "../data/data-layer.tsx";
import { DialogPending } from "../shell/dialog-pending.tsx";
import { lazyWithPreload } from "../shell/lazy.ts";

import type { StepUpOutcome, StepUpRequest } from "./step-up-dialog.tsx";
import type { ReactElement, ReactNode } from "react";

interface StepUpOptions {
  readonly codeOnly?: boolean;
  readonly isForced?: boolean;
}

export type ConfirmIdentity = (reason: string, options?: StepUpOptions) => Promise<StepUpOutcome>;

interface StepUp {
  readonly confirm: ConfirmIdentity;
  readonly verifiedUntil: number | null;
}

const StepUpContext = createContext<StepUp | null>(null);
StepUpContext.displayName = "StepUpContext";

interface Prompt {
  readonly request: StepUpRequest | null;
  readonly close: (outcome: StepUpOutcome) => void;
  readonly register: () => () => void;
}

const PromptContext = createContext<Prompt | null>(null);
PromptContext.displayName = "StepUpPromptContext";

const StepUpDialog = lazyWithPreload(async () => {
  const { StepUpDialog: component } = await import("./step-up-dialog.tsx");
  return component;
});

const LazyStepUpDialog = ({
  request,
  onClose,
}: {
  readonly request: StepUpRequest;
  readonly onClose: (outcome: StepUpOutcome) => void;
}): ReactElement => (
  <Suspense
    fallback={
      <DialogPending
        title={m.step_up_title()}
        onClose={() => {
          onClose("dismissed");
        }}
      />
    }
  >
    <StepUpDialog request={request} onClose={onClose} />
  </Suspense>
);

const usePrompt = (request: StepUpRequest | null, onClosed: () => void) => {
  const [outlets, setOutlets] = useState(0);
  const register = useCallback(() => {
    setOutlets((count) => count + 1);
    return () => {
      setOutlets((count) => count - 1);
    };
  }, []);
  const close = (outcome: StepUpOutcome): void => {
    request?.resolve(outcome);
    onClosed();
  };
  return { prompt: { request, close, register }, outlets };
};

export const StepUpProvider = ({ children }: { readonly children: ReactNode }): ReactElement => {
  const { auth } = useDataLayer();
  const [request, setRequest] = useState<StepUpRequest | null>(null);
  const [verifiedUntil, setVerifiedUntil] = useState<number | null>(null);
  const [checks, setChecks] = useState(0);
  useEffect(
    () =>
      auth.onSessionChange(() => {
        setChecks((count) => count + 1);
      }),
    [auth],
  );
  useEffect(() => {
    const load = async (): Promise<void> => {
      const user = await auth.currentUser();
      setVerifiedUntil(user?.verifiedUntil ?? null);
    };
    void load();
  }, [auth, checks]);
  const confirm = useCallback<ConfirmIdentity>(
    async (reason, options = {}) => {
      const isCodeOnly = options.codeOnly ?? false;
      const isFresh = verifiedUntil !== null && verifiedUntil > Date.now();
      const isReusable = !isCodeOnly && options.isForced !== true;
      if (isReusable && isFresh) {
        return "confirmed";
      }
      return new Promise<StepUpOutcome>((resolve) => {
        setRequest({ reason, codeOnly: isCodeOnly, resolve });
      });
    },
    [verifiedUntil],
  );
  const stepUp = useMemo(() => ({ confirm, verifiedUntil }), [confirm, verifiedUntil]);
  const { prompt, outlets } = usePrompt(request, () => {
    setRequest(null);
    setChecks((count) => count + 1);
  });
  return (
    <StepUpContext value={stepUp}>
      <PromptContext value={prompt}>
        {children}
        {request !== null && outlets === 0 && <LazyStepUpDialog request={request} onClose={prompt.close} />}
      </PromptContext>
    </StepUpContext>
  );
};

const useStepUpContext = (): StepUp => {
  const stepUp = use(StepUpContext);
  if (stepUp === null) {
    throw new Error("useStepUp needs a StepUpProvider");
  }
  return stepUp;
};

export const useConfirmIdentity = (): ConfirmIdentity => useStepUpContext().confirm;

// WHY: a modal dialog makes everything outside it inert, so a step-up asked from inside a dialog must render nested in it.
export const StepUpOutlet = (): ReactElement | null => {
  const prompt = use(PromptContext);
  const register = prompt?.register;
  useEffect(() => register?.(), [register]);
  const request = prompt?.request ?? null;
  return prompt === null || request === null ? null : (
    <LazyStepUpDialog request={request} onClose={prompt.close} />
  );
};

export const useVerifiedUntil = (): number | null => useStepUpContext().verifiedUntil;
